import { useState, useCallback, useRef } from 'react';
import { normalizeDetectedCity } from '../utils/userLocationDisplay';

const GEOCODE_TIMEOUT_MS = 10000;

const fetchWithTimeout = (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS);

  return fetch(url, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timeoutId)
  );
};

/** Normalize provider payloads into { address, country, countryCode, latitude?, longitude? }. */
const buildLocationResult = ({
  city,
  state,
  country,
  countryCode,
  latitude,
  longitude,
}) => {
  const countryName = country?.trim() || null;
  const cityName = city?.trim() || null;
  const stateName = state?.trim() || null;

  let addressString = null;
  if (cityName && stateName) {
    addressString = `${cityName}, ${stateName}`;
  } else if (cityName) {
    addressString = cityName;
  } else if (stateName) {
    addressString = stateName;
  } else if (countryName) {
    addressString = countryName;
  }

  if (!addressString && !countryName) {
    return null;
  }

  const lat = latitude != null && !Number.isNaN(Number(latitude)) ? Number(latitude) : null;
  const lng = longitude != null && !Number.isNaN(Number(longitude)) ? Number(longitude) : null;

  return {
    address: addressString,
    city: cityName,
    state: stateName,
    country: countryName,
    countryCode: countryCode?.trim()?.toUpperCase() || null,
    latitude: lat,
    longitude: lng,
  };
};

const parseBigDataCloud = (data) => {
  if (!data?.countryName && !data?.city) return null;
  return buildLocationResult({
    city: data.city || data.locality,
    state: data.principalSubdivision,
    country: data.countryName,
    countryCode: data.countryCode,
    latitude: data.latitude,
    longitude: data.longitude,
  });
};

const parsePhoton = (data) => {
  const props = data?.features?.[0]?.properties;
  if (!props) return null;
  const coords = data?.features?.[0]?.geometry?.coordinates;
  return buildLocationResult({
    city: props.city || props.district || props.locality,
    state: props.state,
    country: props.country,
    countryCode: props.countrycode,
    longitude: coords?.[0],
    latitude: coords?.[1],
  });
};

const parseGeolocationDb = (data) => {
  if (!data?.country_name && !data?.country_code) return null;
  return buildLocationResult({
    city: data.city,
    state: data.state,
    country: data.country_name,
    countryCode: data.country_code,
    latitude: data.latitude,
    longitude: data.longitude,
  });
};

const reverseGeocodeBigDataCloud = async (lat, lng) => {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const response = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;
  const data = await response.json();
  return parseBigDataCloud(data);
};

const reverseGeocodePhoton = async (lat, lng) => {
  const url = `https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}&lang=en`;
  const response = await fetchWithTimeout(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;
  const data = await response.json();
  return parsePhoton(data);
};

/** Browser-safe providers only (Nominatim blocks CORS / rate-limits from the client). */
const REVERSE_GEOCODE_PROVIDERS = [reverseGeocodeBigDataCloud, reverseGeocodePhoton];

const reverseGeocode = async (lat, lng) => {
  for (const provider of REVERSE_GEOCODE_PROVIDERS) {
    try {
      const result = await provider(lat, lng);
      if (result?.address || result?.country) {
        return { ...result, latitude: lat, longitude: lng };
      }
    } catch (err) {
      if (err?.name !== 'AbortError' && process.env.NODE_ENV === 'development') {
        console.debug('Reverse geocode provider skipped:', err?.message || err);
      }
    }
  }
  return null;
};

const applyNormalizedCity = (loc) => {
  if (!loc) return loc;
  const city = normalizeDetectedCity({
    city: loc.city,
    state: loc.state,
    country: loc.country,
  });
  if (!city || city === loc.city) return loc;
  const state = loc.state?.trim() || null;
  const country = loc.country?.trim() || null;
  const address =
    city && state
      ? `${city}, ${state}`
      : city && country
        ? `${city}, ${country}`
        : city || loc.address;
  return { ...loc, city, address: address || loc.address };
};

const enrichIpLocationWithReverseGeocode = async (ipLoc) => {
  if (!ipLoc || ipLoc.city) return applyNormalizedCity(ipLoc);
  if (ipLoc.latitude == null || ipLoc.longitude == null) return ipLoc;

  const geocoded = await reverseGeocode(ipLoc.latitude, ipLoc.longitude);
  if (!geocoded?.city) return applyNormalizedCity(ipLoc);

  return applyNormalizedCity({
    ...ipLoc,
    city: geocoded.city,
    state: geocoded.state || ipLoc.state,
    address: geocoded.address || ipLoc.address,
    country: geocoded.country || ipLoc.country,
    countryCode: geocoded.countryCode || ipLoc.countryCode,
  });
};

/** IP geolocation — follows VPN exit node (unlike browser GPS). */
const detectLocationByIp = async () => {
  try {
    const response = await fetchWithTimeout(
      'https://api.bigdatacloud.net/data/ip-geolocation-client?localityLanguage=en',
      { headers: { Accept: 'application/json' } }
    );
    if (response.ok) {
      const data = await response.json();
      if (data?.countryName) {
        const built = buildLocationResult({
          city: data.city,
          state: data.principalSubdivision,
          country: data.countryName,
          countryCode: data.countryCode,
          latitude: data.latitude,
          longitude: data.longitude,
        });
        if (built) {
          return enrichIpLocationWithReverseGeocode({ ...built, source: 'ip' });
        }
      }
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('IP geolocation failed (bigdatacloud):', err?.message || err);
    }
  }

  try {
    const response = await fetchWithTimeout('https://geolocation-db.com/json/', {
      headers: { Accept: 'application/json' },
    });
    if (response.ok) {
      const data = await response.json();
      const parsed = parseGeolocationDb(data);
      if (parsed) {
        return enrichIpLocationWithReverseGeocode({ ...parsed, source: 'ip' });
      }
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('IP geolocation failed (geolocation-db):', err?.message || err);
    }
  }

  return null;
};

const normalizeCountryKey = (country, countryCode) =>
  (countryCode || country || '').trim().toLowerCase();

const countriesMatch = (a, b) => {
  if (!a?.country && !b?.country) return true;
  const keyA = normalizeCountryKey(a.country, a.countryCode);
  const keyB = normalizeCountryKey(b.country, b.countryCode);
  if (keyA && keyB && keyA === keyB) return true;
  if (a.country && b.country && a.country.toLowerCase() === b.country.toLowerCase()) return true;
  return false;
};

/** Browser GPS — physical/Wi‑Fi location; does not follow VPN. */
const detectLocationByGps = ({ coordsOnly = false } = {}) =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        if (coordsOnly) {
          resolve({
            latitude,
            longitude,
            source: 'gps',
          });
          return;
        }

        const geocoded = await reverseGeocode(latitude, longitude);
        resolve({
          latitude,
          longitude,
          address: geocoded?.address || null,
          city: geocoded?.city || null,
          state: geocoded?.state || null,
          country: geocoded?.country || null,
          countryCode: geocoded?.countryCode || null,
          source: 'gps',
        });
      },
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  });

/**
 * Prefer IP when it disagrees with GPS (VPN). Use GPS coordinates when countries match.
 */
const mergeIpAndGps = (ipLoc, gpsLoc) => {
  if (ipLoc && gpsLoc) {
    if (!countriesMatch(ipLoc, gpsLoc)) {
      return {
        ...ipLoc,
        source: 'ip',
      };
    }
    const merged = {
      latitude: gpsLoc.latitude,
      longitude: gpsLoc.longitude,
      address: gpsLoc.address || ipLoc.address,
      city: gpsLoc.city || ipLoc.city,
      state: gpsLoc.state || ipLoc.state,
      country: ipLoc.country || gpsLoc.country,
      countryCode: ipLoc.countryCode || gpsLoc.countryCode,
      source: 'merged',
    };
    return applyNormalizedCity(merged);
  }
  return ipLoc || gpsLoc || null;
};

const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState(null);
  const [city, setCity] = useState(null);
  const [state, setState] = useState(null);
  const [country, setCountry] = useState(null);
  const [locationSource, setLocationSource] = useState(null);
  const requestIdRef = useRef(0);

  const applyLocation = useCallback((result) => {
    if (!result) return false;

    if (result.latitude != null && result.longitude != null) {
      setLocation({
        latitude: result.latitude,
        longitude: result.longitude,
        timestamp: Date.now(),
      });
    }

    if (result.address) setAddress(result.address);
    if (result.city) setCity(result.city);
    if (result.state) setState(result.state);
    if (result.country) setCountry(result.country);
    if (result.source) setLocationSource(result.source);

    return Boolean(
      (result.latitude != null && result.longitude != null) || result.country
    );
  }, []);

  const getCurrentLocation = useCallback(async (options = {}) => {
    const { preferIp = true } = options;
    const requestId = ++requestIdRef.current;

    if (!navigator.geolocation && !preferIp) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let result = null;

      if (preferIp) {
        const ipLoc = await detectLocationByIp();
        if (requestId !== requestIdRef.current) return;

        const gpsLoc = await detectLocationByGps();
        if (requestId !== requestIdRef.current) return;

        result = applyNormalizedCity(mergeIpAndGps(ipLoc, gpsLoc));

        if (process.env.NODE_ENV === 'development' && result) {
          console.debug('Location resolved:', {
            source: result.source,
            country: result.country,
            address: result.address,
            ip: ipLoc?.country,
            gps: gpsLoc?.country,
          });
          }
        } else {
        result = applyNormalizedCity(await detectLocationByGps());
        if (requestId !== requestIdRef.current) return;
      }

      if (!result || !applyLocation(result)) {
        setError(
          preferIp
            ? 'Unable to detect your location. Check network or location permissions.'
            : 'Unable to retrieve your location.'
        );
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        console.error('Location detection error:', err);
        setError('Unable to detect your location.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [applyLocation]);

  return {
    location,
    address,
    city,
    state,
    country,
    locationSource,
    error,
    loading,
    getCurrentLocation,
  };
};

export default useGeolocation;
